import type {
  ApprovalGate,
  ImpactLine,
  JournalEntryGroup,
  JournalLine,
  LegalMatter,
  SourceTabSummary,
  UnusualItem,
} from './types';

export const PERIOD_LABEL = 'Apr 2025';
export const JOURNAL_DATE = '04/30/2025';
export const CLIENT_NAME = 'NovaByte Analytics, Inc.';
export const SOURCE_AGENT = 'CrossCore-EC02e';

export const legalMatters: LegalMatter[] = [
  {
    id: 'CTG-2025-01',
    description: 'Patent infringement claim - genomics data processing algorithm',
    counterparty: 'BioPatents LLC (NPE)',
    opened: '2024-08-15',
    assessment:
      'Reasonably possible, not probable. Counsel anticipates motion to dismiss and sees no prior precedent for this claim type in genomics SaaS.',
    probability: 'Reasonably Possible (< 50%)',
    lowEstimate: 75000,
    highEstimate: 450000,
    recommendedAccrual: 0,
    treatment: 'disclose',
    gateId: 'ctg01',
  },
  {
    id: 'CTG-2025-02',
    description: 'Former employee wrongful termination claim (SEV-2024-04)',
    counterparty: 'Ex-Employee (Confidential)',
    opened: '2025-02-01',
    assessment:
      'Probable and estimable. EEOC complaint filed, settlement discussions ongoing, and GC recommends accruing the low estimate.',
    probability: 'Probable and Estimable',
    lowEstimate: 85000,
    highEstimate: 220000,
    recommendedAccrual: 85000,
    treatment: 'accrue',
    gateId: 'ctg02',
  },
  {
    id: 'CTG-2025-03',
    description: 'Customer dispute - data breach notification obligation',
    counterparty: 'Pacific Medical Research',
    opened: '2025-03-20',
    assessment:
      'Reasonably possible. Customer alleges a minor data incident, while security assessment confirms no breach occurred.',
    probability: 'Reasonably Possible (< 25%)',
    lowEstimate: 0,
    highEstimate: 45000,
    recommendedAccrual: 0,
    treatment: 'disclose',
    gateId: 'ctg03',
  },
  {
    id: 'CTG-2025-04',
    description: 'State sales tax audit - California (2022-2023)',
    counterparty: 'California CDTFA',
    opened: '2024-11-01',
    assessment:
      'Probable and estimable. California taxing authority is focused on SaaS taxability for professional services revenue.',
    probability: 'Probable and Estimable',
    lowEstimate: 42000,
    highEstimate: 125000,
    recommendedAccrual: 65000,
    treatment: 'accrue',
    gateId: 'ctg04',
  },
];

export const unusualItems: UnusualItem[] = [
  {
    id: 'UNK-APR-01',
    description: 'Warrant liability mark-to-market for April',
    amount: 60000,
    treatment: 'DR 28210 Warrant Liability / CR 70900 Other Income',
    judgmentRequired: 'Tech Accounting validates Black-Scholes inputs. CFO approves remeasurement.',
    standard: 'ASC 480',
    gateId: 'warrant',
  },
  {
    id: 'UNK-APR-02',
    description: 'FX revaluation - Prague CZK/USD monetary items',
    amount: -18500,
    treatment: 'DR 38000 Unrealized FX Loss / CR Prague monetary accounts',
    judgmentRequired: 'Controller validates FX rates against Refinitiv closing source data.',
    standard: 'ASC 830',
    gateId: 'fx',
  },
  {
    id: 'UNK-APR-03',
    description: 'CTA reclassification - Prague translation adjustment',
    amount: 12200,
    treatment: 'DR 37400 CTA / CR Equity OCI',
    judgmentRequired: 'Senior Accounting reviews translation methodology. OCI only.',
    standard: 'ASC 830',
    gateId: 'cta',
  },
];

export const approvalGates: ApprovalGate[] = [
  {
    id: 'ctg02',
    title: 'CTG-2025-02 - Wrongful termination accrual',
    subtitle: 'GC written certification and CFO approval required before posting the $85,000 accrual.',
    type: 'HITL',
    approver: 'GC Blaine Evans + CFO Waverly Morgan',
    approveLabel: 'GC + CFO Approve',
  },
  {
    id: 'ctg04',
    title: 'CTG-2025-04 - California tax audit accrual',
    subtitle: 'GC, tax counsel, and CFO approval required before posting the $65,000 reserve.',
    type: 'HITL',
    approver: 'GC + Tax Counsel + CFO Waverly Morgan',
    approveLabel: 'Approve',
  },
  {
    id: 'ctg01',
    title: 'CTG-2025-01 - BioPatents disclosure classification',
    subtitle: 'GC confirms reasonably possible classification and footnote disclosure treatment.',
    type: 'HITL',
    approver: 'GC Blaine Evans',
    approveLabel: 'GC Confirms',
  },
  {
    id: 'ctg03',
    title: 'CTG-2025-03 - Pacific Medical disclosure classification',
    subtitle: 'GC confirms reasonably possible classification before financial statement disclosure.',
    type: 'HITL',
    approver: 'GC Blaine Evans',
    approveLabel: 'GC Confirms',
  },
  {
    id: 'warrant',
    title: 'UNK-APR-01 - Warrant liability MTM',
    subtitle: 'Tech Accounting validates Black-Scholes inputs; CFO reviews the non-cash P&L impact.',
    type: 'HOTL',
    approver: 'Marlowe Mitchell (Tech Accounting) + CFO',
    approveLabel: 'Tech + CFO Approve',
  },
  {
    id: 'fx',
    title: 'UNK-APR-02 - Prague FX revaluation',
    subtitle: 'Controller validates closing rates and approves the standard monthly ASC 830 entry.',
    type: 'HOTL',
    approver: 'Controller',
    approveLabel: 'Controller Approves',
  },
  {
    id: 'cta',
    title: 'UNK-APR-03 - Prague CTA adjustment',
    subtitle: 'Senior Accounting reviews the translation methodology and OCI-only treatment.',
    type: 'HOTL',
    approver: 'Senior Accounting',
    approveLabel: 'Senior Accounting Approves',
  },
];

const line = (
  externalId: string,
  memo: string,
  accountNumber: string,
  accountName: string,
  debit: number | null,
  credit: number | null,
  department: string,
  className: string,
  subsidiary: string,
  approver: string,
  gateId: string,
): JournalLine => ({
  externalId,
  journalDate: JOURNAL_DATE,
  memo,
  accountNumber,
  accountName,
  debit,
  credit,
  department,
  className,
  subsidiary,
  approver,
  gateId,
});

export const journalEntries: JournalEntryGroup[] = [
  {
    id: 'JE-1',
    title: 'CTG-2025-02 - Wrongful termination',
    standard: 'ASC 450-20-30',
    basis: 'Probable and estimable. EEOC complaint filed and settlement discussions are active.',
    gateId: 'ctg02',
    lines: [
      line('EC02e-APR25-JE01-L1', 'CTG-2025-02 Wrongful Termination Accrual ASC 450-20-30', '62410', 'Legal Settlements', 85000, null, 'Legal', 'G&A', 'NovaByte US', 'GC Blaine Evans + CFO Waverly Morgan', 'ctg02'),
      line('EC02e-APR25-JE01-L2', 'CTG-2025-02 Wrongful Termination Accrual ASC 450-20-30', '21060', 'Legal Contingency Reserve', null, 85000, 'Legal', 'G&A', 'NovaByte US', 'GC Blaine Evans + CFO Waverly Morgan', 'ctg02'),
    ],
  },
  {
    id: 'JE-2',
    title: 'CTG-2025-04 - California tax audit',
    standard: 'ASC 450-20-30 / ASC 740',
    basis: 'Probable exposure on California SaaS professional services revenue for 2022-2023.',
    gateId: 'ctg04',
    lines: [
      line('EC02e-APR25-JE02-L1', 'CTG-2025-04 CA CDTFA Tax Audit Reserve ASC 450/740', '64510', 'State Tax Expense', 65000, null, 'Tax', 'G&A', 'NovaByte US', 'GC + Tax Counsel + CFO', 'ctg04'),
      line('EC02e-APR25-JE02-L2', 'CTG-2025-04 CA CDTFA Tax Audit Reserve ASC 450/740', '21065', 'Tax Contingency Reserve', null, 65000, 'Tax', 'G&A', 'NovaByte US', 'GC + Tax Counsel + CFO', 'ctg04'),
    ],
  },
  {
    id: 'JE-3',
    title: 'UNK-APR-01 - Warrant MTM',
    standard: 'ASC 480',
    basis: 'Quarterly remeasurement of warrant liability using validated Black-Scholes inputs.',
    gateId: 'warrant',
    lines: [
      line('EC02e-APR25-JE03-L1', 'UNK-APR-01 Warrant Liability MTM Gain ASC 480', '28210', 'Warrant Liability', 60000, null, 'Finance', 'G&A', 'NovaByte US', 'Marlowe Mitchell + CFO', 'warrant'),
      line('EC02e-APR25-JE03-L2', 'UNK-APR-01 Warrant Liability MTM Gain ASC 480', '70900', 'Other Income MTM Gain', null, 60000, 'Finance', 'G&A', 'NovaByte US', 'Marlowe Mitchell + CFO', 'warrant'),
    ],
  },
  {
    id: 'JE-4',
    title: 'UNK-APR-02 - Prague FX revaluation',
    standard: 'ASC 830',
    basis: 'Monthly revaluation of Prague CZK monetary balances at the closing rate.',
    gateId: 'fx',
    lines: [
      line('EC02e-APR25-JE04-L1', 'UNK-APR-02 FX Revaluation Prague CZK ASC 830', '38000', 'Unrealized FX Loss', 18500, null, 'Finance', 'G&A', 'NovaByte CZ', 'Controller', 'fx'),
      line('EC02e-APR25-JE04-L2', 'UNK-APR-02 FX Revaluation Prague CZK ASC 830', 'Prague-Monetary', 'Prague Monetary Accounts', null, 18500, 'Finance', 'G&A', 'NovaByte CZ', 'Controller', 'fx'),
    ],
  },
  {
    id: 'JE-5',
    title: 'UNK-APR-03 - CTA adjustment',
    standard: 'ASC 830',
    basis: 'OCI-only translation adjustment for Prague subsidiary balances.',
    gateId: 'cta',
    lines: [
      line('EC02e-APR25-JE05-L1', 'UNK-APR-03 CTA Translation Adjustment OCI ASC 830', '37400', 'CTA OCI Equity', 12200, null, 'Finance', 'G&A', 'NovaByte CZ', 'Senior Accounting', 'cta'),
      line('EC02e-APR25-JE05-L2', 'UNK-APR-03 CTA Translation Adjustment OCI ASC 830', 'Equity-OCI', 'Equity OCI Translation', null, 12200, 'Finance', 'G&A', 'NovaByte CZ', 'Senior Accounting', 'cta'),
    ],
  },
];

export const impactLines: ImpactLine[] = [
  { label: 'Legal Settlements Expense', account: '62410', amount: -85000, driver: 'CTG-2025-02 wrongful termination', statement: 'P&L' },
  { label: 'State Tax Expense', account: '64510', amount: -65000, driver: 'CTG-2025-04 CA tax audit', statement: 'P&L' },
  { label: 'Other Income - Warrant MTM', account: '70900', amount: 60000, driver: 'ASC 480 non-cash MTM gain', statement: 'P&L' },
  { label: 'FX Loss', account: '38000', amount: -18500, driver: 'Prague CZK revaluation', statement: 'P&L' },
  { label: 'CTA Adjustment', account: '37400', amount: 12200, driver: 'Prague translation', statement: 'OCI' },
];

export const sourceTabs: SourceTabSummary[] = [
  {
    name: 'Legal_Contingency_Log',
    purpose: 'Active ASC 450 legal matters, GC assessments, probability classification, estimate range, and CFO approval placeholders.',
    rows: 12,
    columns: 11,
    notes: ['4 active matters', '2 probable and estimable', '2 reasonably possible disclosures'],
  },
  {
    name: 'Restructuring_Reserve_ASC420',
    purpose: 'ASC 420 reserve template retained for future board-approved restructuring plans.',
    rows: 3,
    columns: 10,
    notes: ['No active restructuring plan for Apr 2025', 'Template only'],
  },
  {
    name: 'IBNR_Medical_Reserve',
    purpose: 'Self-insurance reserve template retained if the company moves away from fully insured medical coverage.',
    rows: 6,
    columns: 8,
    notes: ['Not applicable for Apr 2025', 'Company is fully insured through Aetna PPO/HMO'],
  },
  {
    name: 'Unusual_Items_Log',
    purpose: 'Controller judgment items for warrant MTM, FX revaluation, and CTA adjustment.',
    rows: 5,
    columns: 8,
    notes: ['3 active unusual items', 'ASC 480 and ASC 830 treatments'],
  },
];

export const disclosureMatters = legalMatters.filter(matter => matter.treatment === 'disclose');
export const probableMatters = legalMatters.filter(matter => matter.treatment === 'accrue');
export const allJournalLines = journalEntries.flatMap(entry => entry.lines);
